// Recurring Invoice — the generated-invoice listing (spec §3) and the Recurring
// Invoice Report (spec §4.2).
import React, { useMemo, useState } from "react";
import { Btn, Counters, CustCell, DataGrid, FDate, FSearch, FSelect, IC, RowBtn, Seg, StatusPill, TypeTag, opts } from "./RecurringParts";
import { distinct } from "./ProfileScreens";
import { INVOICE_TYPES, PAY_STATUSES, addMonths, compact, daysTo, fmtD, fmtDLong, iso, monthKey, monthLbl, money2, sumNet, today } from "./recurringModel";
import { INVOICE_HEAD, exportTable, invoiceCells } from "./recurringExport";

export const BLANK_INF = { q: "", cust: "", sales: "", serv: "", type: "", from: "", to: "", pay: "Pending", counter: "" };

export const filterInvoices = (rows, f) => {
  const needle = (f.q || "").trim().toLowerCase();
  return rows.filter((x) => {
    if (f.pay && x.pay !== f.pay) return false;
    if (f.cust && x.custGuid !== f.cust) return false;
    if (f.sales && x.salesGuid !== f.sales) return false;
    if (f.serv && x.servGuid !== f.serv) return false;
    if (f.type && x.type !== f.type) return false;
    if (f.from && x.date < f.from) return false;
    if (f.to && x.date > f.to) return false;
    if (f.counter === "overdue" && !(x.pay === "Pending" && daysTo(x.due) < 0)) return false;
    if (needle) {
      const hay = [x.no, x.custName, x.salesperson, x.service, x.type, x.profileNo].join(" ").toLowerCase();
      if (hay.indexOf(needle) < 0) return false;
    }
    return true;
  });
};

const groupOf = (x, g) =>
  g === "salesperson" ? x.salesperson : g === "service" ? x.service : g === "month" ? monthLbl(monthKey(x.date)) : x.type;
const amountOf = (x) => x.net;

const Actions = ({ x, permit, act, short }) => (
  <span className="axrc-rowacts">
    <RowBtn title="View" icon={IC.view} onClick={() => act.view(x)} />
    {permit.edit && x.pay === "Pending" && <RowBtn title="Edit" icon={IC.edit} onClick={() => act.edit(x)} />}
    {permit.add && x.pay === "Pending" && <RowBtn kind="pay" title="Receive payment & make invoice" icon={IC.cash} onClick={() => act.receive(x)} />}
    {!short && <RowBtn title="Print" icon={IC.print} onClick={() => act.print(x)} />}
    {!short && <RowBtn title="Email" icon={IC.mail} onClick={() => act.email(x)} />}
    {permit.deletion && x.pay === "Pending" && <RowBtn kind="del" title="Cancel" icon={IC.cancel} onClick={() => act.cancel(x)} />}
  </span>
);

const LateTag = ({ x }) => {
  const n = daysTo(x.due);
  return x.pay === "Pending" && n < 0 ? <span className="axrc-late">{Math.abs(n)}d late</span> : null;
};

/* ================================================================ listing */

export const InvoiceList = ({ invoices, inf, setInf, permit, act, onReload, cur, onRange }) => {
  const rows = useMemo(() => filterInvoices(invoices, inf), [invoices, inf]);
  const set = (k) => (v) => {
    setInf((f) => ({ ...f, [k]: v, counter: k === "pay" ? "" : f.counter }));
    if (k === "from" && v && onRange) onRange(v);
  };
  const cols = useMemo(
    () => [
      { k: "custName", t: "Customer", render: (x) => <CustCell name={x.custName} sub={(x.no || "—") + (x.type ? " · " + x.type : "")} /> },
      { k: "salesperson", t: "Sales person", w: "130px" },
      { k: "service", t: "Main service", w: "190px", render: (x) => <span className="axrc-ell">{x.service || "—"}</span> },
      {
        k: "date",
        t: "Invoice date",
        w: "150px",
        render: (x) => (
          <>
            {fmtD(x.date)}
            <span className="axrc-dim small"> · due {fmtD(x.due)}</span>
          </>
        ),
      },
      { k: "net", t: "Amount", num: true, w: "112px", render: (x) => money2(x.net) },
      {
        k: "pay",
        t: "Payment status",
        w: "140px",
        render: (x) => (
          <>
            <StatusPill s={x.pay} /> <LateTag x={x} />
          </>
        ),
      },
      { k: "acts", t: "", w: "190px", nosort: true, render: (x) => <Actions x={x} permit={permit} act={act} /> },
    ],
    [permit, act]
  );

  const mk = monthKey(iso(today()));
  const pending = invoices.filter((x) => x.pay === "Pending");
  const overdue = pending.filter((x) => daysTo(x.due) < 0);
  const paid = invoices.filter((x) => x.pay === "Paid");
  const thisM = invoices.filter((x) => monthKey(x.date) === mk);
  const monthOn = inf.from === iso(new Date(today().getFullYear(), today().getMonth(), 1)) && !inf.pay && !inf.counter;
  const items = [
    { k: "", l: "All invoices", v: invoices.length, s: cur + " " + compact(sumNet(invoices)), on: !inf.pay && !inf.counter && !monthOn },
    { k: "Pending", l: "Pending", v: pending.length, cls: "warn", s: cur + " " + compact(sumNet(pending)), on: inf.pay === "Pending" && !inf.counter },
    { k: "overdue", l: "Overdue", v: overdue.length, cls: "bad", s: cur + " " + compact(sumNet(overdue)), on: inf.counter === "overdue" },
    { k: "Paid", l: "Paid", v: paid.length, cls: "good", s: cur + " " + compact(sumNet(paid)), on: inf.pay === "Paid" },
    { k: "month", l: "This month", v: thisM.length, cls: "acc", s: cur + " " + compact(sumNet(thisM)), on: monthOn },
  ];
  const pick = (k) => {
    if (k === "overdue") return setInf((f) => ({ ...f, pay: "Pending", counter: "overdue" }));
    if (k === "month")
      return setInf((f) => ({ ...f, pay: "", counter: "", from: iso(new Date(today().getFullYear(), today().getMonth(), 1)), to: iso(today()) }));
    return setInf((f) => ({ ...f, pay: k, counter: "" }));
  };
  const due = sumNet(rows.filter((x) => x.pay === "Pending"));
  const doExport = (kind) => exportTable(kind, "recurring-invoices", "Recurring invoices", INVOICE_HEAD, rows.map(invoiceCells));

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Recurring invoices</h1>
          <div className="sub">Everything the scheduler has raised from the profiles</div>
        </div>
        <div className="sp" />
        <div className="axrc-btngrp">
          <Btn kind="icon" title="Refresh" aria-label="Refresh" icon={IC.refresh} onClick={onReload} />
        </div>
        <div className="axrc-btngrp">
          <Btn onClick={() => doExport("csv")}>CSV</Btn>
          <Btn onClick={() => doExport("xlsx")}>Excel</Btn>
          <Btn kind="icon" title="Print" aria-label="Print" icon={IC.print} onClick={() => doExport("print")} />
        </div>
      </div>

      <Counters items={items} cols={5} onPick={pick} />

      <div className="axrc-filters">
        <FSearch id="rcISearch" value={inf.q} onChange={set("q")} placeholder="Invoice no, customer, service…" />
        <FSelect id="rcICust" label="Customer" value={inf.cust} onChange={set("cust")} options={distinct(invoices, "custGuid", "custName")} all="All customers" />
        <FSelect id="rcISales" label="Sales person" w="w1" value={inf.sales} onChange={set("sales")} options={distinct(invoices, "salesGuid", "salesperson")} all="All salespeople" />
        <FSelect id="rcIServ" label="Main service" value={inf.serv} onChange={set("serv")} options={distinct(invoices, "servGuid", "service")} all="All services" />
        <FDate id="rcIFrom" label="Invoice from" value={inf.from} onChange={set("from")} />
        <FDate id="rcITo" label="To" value={inf.to} onChange={set("to")} />
        <FSelect id="rcIPay" label="Payment status" w="w3" value={inf.pay} onChange={set("pay")} options={opts(PAY_STATUSES)} all="All" on />
        <div className="axrc-fl">
          <label>&nbsp;</label>
          <Btn onClick={() => setInf(BLANK_INF)}>Clear</Btn>
        </div>
      </div>

      <DataGrid
        cols={cols}
        rows={rows}
        rowKey={(x) => x.guid}
        initialSort={{ k: "date", dir: -1 }}
        onRowClick={act.view}
        emptyText={invoices.length ? "No invoice matches these filters." : "The scheduler has not raised any invoice yet."}
        foot={(r) => (
          <tr>
            <td colSpan={4}>Total · {r.length} invoices</td>
            <td className="num">{money2(sumNet(r))}</td>
            <td colSpan={2} />
          </tr>
        )}
        footBar={
          <span>
            Outstanding <b>{money2(due)}</b> · Collected <b>{money2(sumNet(rows) - due)}</b>
          </span>
        }
      />
    </section>
  );
};

/* ================================================================ report */

export const BLANK_RIF = { cust: "", sales: "", serv: "", type: "", from: "", to: "", pay: "Pending" };

export const InvoiceReport = ({ invoices, permit, act, cur, onRange }) => {
  const [draft, setDraft] = useState(BLANK_RIF);
  const [applied, setApplied] = useState(BLANK_RIF);
  const [group, setGroup] = useState("");
  const [ranAt, setRanAt] = useState(new Date());
  const rows = useMemo(() => filterInvoices(invoices, applied), [invoices, applied]);
  const set = (k) => (v) => setDraft((f) => ({ ...f, [k]: v }));
  const cols = useMemo(
    () => [
      { k: "custName", t: "Customer", render: (x) => <CustCell name={x.custName} sub={x.no} /> },
      { k: "salesperson", t: "Sales person", w: "130px" },
      { k: "service", t: "Main service", w: "180px", render: (x) => <span className="axrc-ell">{x.service || "—"}</span> },
      { k: "type", t: "Invoice type", w: "124px", render: (x) => <TypeTag t={x.type} /> },
      { k: "date", t: "Invoice date", w: "120px", render: (x) => fmtD(x.date) },
      { k: "net", t: "Amount", num: true, w: "112px", render: (x) => money2(x.net) },
      {
        k: "pay",
        t: "Payment status",
        w: "140px",
        render: (x) => (
          <>
            <StatusPill s={x.pay} /> <LateTag x={x} />
          </>
        ),
      },
      { k: "acts", t: "", w: "128px", nosort: true, render: (x) => <Actions x={x} permit={permit} act={act} short /> },
    ],
    [permit, act]
  );
  const net = sumNet(rows);
  const due = sumNet(rows.filter((x) => x.pay === "Pending"));
  const overdue = rows.filter((x) => x.pay === "Pending" && daysTo(x.due) < 0);
  const doExport = (kind) => exportTable(kind, "recurring-invoice-report", "Recurring Invoice Report", INVOICE_HEAD, rows.map(invoiceCells));
  const run = () => {
    setApplied(draft);
    setRanAt(new Date());
    // the page keeps about 15 months loaded; an earlier "from" asks for more
    if (draft.from && onRange) onRange(draft.from);
  };

  return (
    <section className="axrc-screen">
      <div className="axrc-phead">
        <div>
          <h1>Recurring Invoice Report</h1>
          <div className="sub">Every invoice the scheduler has raised, with where the money stands</div>
        </div>
        <div className="sp" />
        <Seg
          options={[
            { v: "", l: "No grouping" },
            { v: "salesperson", l: "By salesperson" },
            { v: "service", l: "By service" },
            { v: "type", l: "By type" },
            { v: "month", l: "By month" },
          ]}
          value={group}
          onChange={setGroup}
        />
        <div className="axrc-btngrp">
          <Btn onClick={() => doExport("csv")}>CSV</Btn>
          <Btn onClick={() => doExport("xlsx")}>Excel</Btn>
          <Btn onClick={() => doExport("pdf")}>PDF</Btn>
          <Btn kind="icon" title="Print" aria-label="Print" icon={IC.print} onClick={() => doExport("print")} />
        </div>
      </div>
      <div className="axrc-filters">
        <FSelect id="rcRiCust" label="Customer" value={draft.cust} onChange={set("cust")} options={distinct(invoices, "custGuid", "custName")} all="All customers" />
        <FSelect id="rcRiSales" label="Sales person" w="w1" value={draft.sales} onChange={set("sales")} options={distinct(invoices, "salesGuid", "salesperson")} all="All salespeople" />
        <FSelect id="rcRiServ" label="Main service" value={draft.serv} onChange={set("serv")} options={distinct(invoices, "servGuid", "service")} all="All services" />
        <FSelect id="rcRiType" label="Invoice type" w="w3" value={draft.type} onChange={set("type")} options={opts(INVOICE_TYPES)} all="All types" />
        <FDate id="rcRiFrom" label="Invoice from" value={draft.from} onChange={set("from")} />
        <FDate id="rcRiTo" label="To" value={draft.to} onChange={set("to")} />
        <FSelect id="rcRiPay" label="Payment status" w="w3" value={draft.pay} onChange={set("pay")} options={opts(PAY_STATUSES)} all="All" on />
        <div className="sp" />
        <div className="axrc-fl">
          <label>&nbsp;</label>
          <Btn kind="primary" onClick={run}>
            Run report
          </Btn>
        </div>
      </div>
      <Counters
        cols={4}
        items={[
          { k: "a", l: "Invoices", v: rows.length },
          { k: "b", l: "Value", v: cur + " " + compact(net) },
          { k: "c", l: "Outstanding", v: cur + " " + compact(due), cls: "warn" },
          { k: "d", l: "Overdue", v: overdue.length + " · " + cur + " " + compact(sumNet(overdue)), cls: "bad" },
        ]}
      />
      <DataGrid
        cols={cols}
        rows={rows}
        rowKey={(x) => x.guid}
        initialSort={{ k: "date", dir: -1 }}
        group={group}
        groupOf={groupOf}
        amountOf={amountOf}
        onRowClick={act.view}
        foot={(r) => (
          <tr>
            <td colSpan={5}>
              Report total · {r.length} invoices · VAT {money2(r.reduce((a, x) => a + x.vat, 0))}
            </td>
            <td className="num">{money2(sumNet(r))}</td>
            <td colSpan={2} />
          </tr>
        )}
        footBar={
          <span>
            Run {fmtDLong(iso(ranAt))} · {applied.pay || "all payment statuses"}
          </span>
        }
      />
    </section>
  );
};

// the window the page loads by default: the first of the month, 14 months back
export const defaultInvoiceFrom = () => {
  const d = addMonths(today(), -14);
  return iso(new Date(d.getFullYear(), d.getMonth(), 1));
};
